/**
 * TabletsPage — this company's shared tablets, and "Sign out now" (OPEN-036 / DEC-335).
 *
 * Master spec §7: "Remote clear: Dispatcher can force-logout from web console." GET
 * /v1/devices lists the tablets; POST /v1/devices/:id/clear (with `{}` — a body-less JSON
 * POST 500s in Fastify's parser, DEC-201/287) asks one to sign out. The tablet hears it at
 * its next check-in, so the row says "Waiting for the tablet to connect" until it does.
 *
 * What the button does is said on the page, because a dispatcher reaching for it after a
 * lost tablet needs to know its limits: it signs out whoever is on the tablet. It does not
 * lock the driver's account or touch their own phone, and unsent tickets still send.
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation, useApiOpts } from '@lodex-pro/web-shared';
import { Button } from 'src/components/ui/button';
import {
  SHARED_DEVICES_QUERY_KEY,
  useSharedDevices,
  type SharedDeviceRow,
} from '../features/tablets/use-shared-devices';
import { clearStatusText, lastSeenText } from '../features/tablets/tablet-status';
import { useUserProfile } from '../lib/use-user-timezone';
import { TABLET_ENROLL_ROLES, TABLET_ROLES } from '../lib/role-gates';
import { AddTabletDialog } from '../features/tablets/AddTabletDialog';

const cellStyle: React.CSSProperties = {
  padding: '10px 16px',
  borderBottom: '1px solid var(--color-border-primary)',
  fontSize: '14px',
  verticalAlign: 'top',
};
const headStyle: React.CSSProperties = {
  ...cellStyle,
  fontSize: '12px',
  fontWeight: 600,
  color: 'var(--color-text-secondary)',
  textAlign: 'left',
};

function TabletRow({ row, nowMs }: { row: SharedDeviceRow; nowMs: number }): React.JSX.Element {
  const apiOpts = useApiOpts();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = React.useState(false);

  const mutation = useApiMutation<Record<string, never>, { clearRequestedAt: string }>(
    apiOpts,
    `/v1/devices/${row.id}/clear`,
    'POST',
    {
      onSuccess: () => {
        setConfirming(false);
        void queryClient.invalidateQueries({ queryKey: SHARED_DEVICES_QUERY_KEY });
        toast.success(`${row.name} will sign out the next time it connects`);
      },
      onError: () => {
        toast.error('Could not sign the tablet out. Try again.');
      },
    },
  );

  const status = clearStatusText(row, nowMs);

  return (
    <tr data-testid={`tablet-row-${row.id}`}>
      <td style={cellStyle}>{row.name}</td>
      <td style={cellStyle}>{lastSeenText(row, nowMs)}</td>
      <td style={{ ...cellStyle, color: 'var(--color-text-secondary)' }}>{status ?? '—'}</td>
      <td style={{ ...cellStyle, whiteSpace: 'nowrap' }}>
        {confirming ? (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <span style={{ fontSize: '13px' }}>Sign out {row.name}?</span>
            <Button
              type="button"
              variant="destructive"
              disabled={mutation.isPending}
              onClick={(): void => mutation.mutate({})}
            >
              Sign out
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={mutation.isPending}
              onClick={(): void => setConfirming(false)}
            >
              Cancel
            </Button>
          </div>
        ) : (
          <Button type="button" variant="outline" onClick={(): void => setConfirming(true)}>
            Sign out now
          </Button>
        )}
      </td>
    </tr>
  );
}

export function TabletsPage(): React.JSX.Element {
  const apiOpts = useApiOpts();
  const { data: profileData } = useUserProfile();
  const role = profileData?.profile.role ?? '';
  const allowed = TABLET_ROLES.has(role);
  const canEnroll = TABLET_ENROLL_ROLES.has(role);
  const [adding, setAdding] = React.useState(false);
  const { data, isLoading, isError, dataUpdatedAt } = useSharedDevices(apiOpts, allowed);

  const nowMs = React.useMemo(
    () => (dataUpdatedAt > 0 ? Math.max(dataUpdatedAt, Date.now()) : Date.now()),
    [dataUpdatedAt],
  );
  const devices = data?.devices ?? [];

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        backgroundColor: 'var(--color-bg-primary)',
        color: 'var(--color-text-primary)',
      }}
    >
      <div
        style={{
          padding: '12px 16px',
          borderBottom: '1px solid var(--color-border-primary)',
          fontSize: '13px',
          color: 'var(--color-text-secondary)',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
        }}
      >
        <div style={{ flex: 1 }}>
          Shared truck tablets. &quot;Sign out now&quot; signs out whoever is on the tablet the next
          time it connects. It does not lock the driver&apos;s account or affect their own phone,
          and tickets not yet sent will still send.
        </div>
        {canEnroll && (
          <Button
            type="button"
            onClick={(): void => setAdding(true)}
            style={{ minHeight: '56px', fontSize: '16px', flexShrink: 0 }}
          >
            Add a tablet
          </Button>
        )}
      </div>
      {canEnroll && <AddTabletDialog open={adding} onClose={(): void => setAdding(false)} />}
      <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        {!allowed ? (
          <p style={{ padding: '16px', color: 'var(--color-text-tertiary)' }}>
            You do not have access to tablets.
          </p>
        ) : isError ? (
          <p role="alert" style={{ padding: '16px', color: 'var(--color-danger)' }}>
            Could not load tablets.
          </p>
        ) : isLoading ? (
          <p style={{ padding: '16px', color: 'var(--color-text-tertiary)' }}>Loading…</p>
        ) : devices.length === 0 ? (
          <p style={{ padding: '16px', color: 'var(--color-text-tertiary)' }}>
            No tablets yet. A tablet appears here once it is enrolled, or the first time an
            already-enrolled tablet connects.
          </p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={headStyle}>Tablet</th>
                <th style={headStyle}>Last seen</th>
                <th style={headStyle}>Status</th>
                <th style={headStyle}>
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {devices.map((d) => (
                <TabletRow key={d.id} row={d} nowMs={nowMs} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
